import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  Calendar, Clock, Award, AlertCircle, CheckCircle, ArrowRight,
  BookOpen, Globe, Star, ChevronDown, ChevronUp
} from 'lucide-react';


const DELE_FEES = {
  generales: [
    { level: 'A1', base: '₹5,700', centre: '₹800', total: '₹6,500/-' },
    { level: 'A2', base: '₹6,200', centre: '₹850', total: '₹7,050/-' },
    { level: 'B1', base: '₹6,800', centre: '₹900', total: '₹7,700/-' },
    { level: 'B2', base: '₹8,000', centre: '₹1,000', total: '₹9,000/-' },
    { level: 'C1', base: '₹8,500', centre: '₹1,300', total: '₹9,800/-' },
    { level: 'C2', base: '₹9,000', centre: '₹1,500', total: '₹10,500/-' },
  ],
  escolares: [
    { level: 'A1', base: '₹5,700', centre: '₹800', total: '₹6,500/-' },
    { level: 'A2 / B1', base: '₹6,800', centre: '₹900', total: '₹7,700/-' },
    { level: 'B2 / C1', base: '₹8,500', centre: '₹1,300', total: '₹9,800/-' },
  ]
};


function ExamCard({ exam }) {
  const today = new Date();
  const deadline = new Date(exam.deadline);
  const isOpen = today < deadline;
  const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
  const examDate = exam.examDate ? new Date(exam.examDate) : null;

  return (
    <div className="bg-white rounded-2xl shadow-card hover:shadow-float hover:-translate-y-1 transition-all duration-300 p-5 sm:p-8 flex flex-col">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-5 h-5 text-primary-container" />
            <h3 className="font-display font-bold text-xl text-onSurface">{exam.date}</h3>
          </div>
          <p className="text-sm text-onSurfaceVariant">{exam.description}</p>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${isOpen ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-surface-high text-secondary'}`}>
            {isOpen ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {isOpen ? 'Open' : 'Closed'}
          </span>
          {exam.learnerType && exam.learnerType !== 'both' && (
            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${exam.learnerType === 'young' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
              {exam.learnerType === 'young' ? 'Young Learner' : 'General'}
            </span>
          )}
        </div>
      </div>

      {/* Exam Date */}
      {examDate && (
        <div className="flex items-center gap-3 p-3 bg-primary-light/10 border border-primary-light/30 rounded-xl mb-3">
          <Calendar className="w-4 h-4 text-primary-container flex-shrink-0" />
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-primary-container">Examination Date</p>
            <p className="font-bold text-onSurface text-sm">
              {examDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>
      )}

      {/* Registration Deadline */}
      <div className="flex items-center gap-3 p-3 bg-surface-low rounded-xl mb-5">
        <Clock className="w-4 h-4 text-onSurfaceVariant flex-shrink-0" />
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-onSurfaceVariant">Registration Deadline</p>
          <p className="font-semibold text-onSurface text-sm">
            {deadline.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            {isOpen && daysLeft > 0 && <span className="ml-2 text-primary-container text-xs font-bold">({daysLeft} days left)</span>}
          </p>
        </div>
      </div>

      {/* Levels */}
      <div className="mb-6 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-onSurfaceVariant mb-3">Available Levels</p>
        <div className="flex flex-wrap gap-2">
          {exam.levels?.map((level) => (
            <span key={level} className={`px-3 py-1 text-xs font-bold rounded-full border ${
              exam.learnerType === 'young'
                ? 'bg-blue-50 text-blue-700 border-blue-100'
                : 'bg-primary-light/30 text-primary-dark border-transparent'
            }`}>
              {level}
            </span>
          ))}
        </div>
      </div>

      {isOpen ? (
        exam.link ? (
          <a href={exam.link} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-3 bg-primary-container text-white font-bold rounded-xl hover:bg-primary transition-colors text-sm uppercase tracking-wide">
            Register <ArrowRight className="w-4 h-4" />
          </a>
        ) : (
          <Link to="/contact" className="flex items-center justify-center gap-2 w-full py-3 bg-primary-container text-white font-bold rounded-xl hover:bg-primary transition-colors text-sm uppercase tracking-wide">
            Register Interest <ArrowRight className="w-4 h-4" />
          </Link>
        )
      ) : (
        <button disabled className="w-full py-3 bg-surface-high text-secondary font-bold rounded-xl text-sm uppercase tracking-wide cursor-not-allowed opacity-60">
          Registration Closed
        </button>
      )}
    </div>
  );
}

// Helper to extract month and year from exam
const getExamMonthYear = (exam) => {
  if (exam.examDate) {
    const d = new Date(exam.examDate);
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleDateString('en-US', { month: 'long' });
      const year = d.getFullYear();
      return { month, year };
    }
  }
  if (exam.date) {
    const parts = exam.date.trim().split(/\s+/);
    if (parts.length >= 2) {
      const monthPart = parts[0];
      const yearPart = parts[parts.length - 1];
      if (/^\d{4}$/.test(yearPart)) {
        return { month: monthPart, year: parseInt(yearPart, 10) };
      }
    }
  }
  if (exam.deadline) {
    const d = new Date(exam.deadline);
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleDateString('en-US', { month: 'long' });
      const year = d.getFullYear();
      return { month, year };
    }
  }
  return { month: 'Other', year: '' };
};

// Group exams by month and year
const groupExams = (list) => {
  const groups = [];
  list.forEach((exam) => {
    const { month, year } = getExamMonthYear(exam);
    const groupName = year ? `${month} ${year}` : month;
    let group = groups.find((g) => g.name === groupName);
    if (!group) {
      group = { name: groupName, exams: [] };
      groups.push(group);
    }
    group.exams.push(exam);
  });
  return groups;
};

export default function Exams() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarImg, setCalendarImg] = useState(null);
  const [feeCategory, setFeeCategory] = useState('generales');
  const [expandedMonths, setExpandedMonths] = useState({});

  const groupedExams = groupExams(exams);

  // Initialize expanded months when exams load
  useEffect(() => {
    if (exams.length > 0) {
      const initialExpanded = {};
      const grouped = groupExams(exams);
      if (grouped.length > 0) {
        // Expand the first group by default
        initialExpanded[grouped[0].name] = true;
        // Expand any group that has at least one active/open exam
        grouped.forEach((g) => {
          const hasOpen = g.exams.some((exam) => {
            const today = new Date();
            const deadline = new Date(exam.deadline);
            return today < deadline;
          });
          if (hasOpen) {
            initialExpanded[g.name] = true;
          }
        });
      }
      setExpandedMonths(initialExpanded);
    }
  }, [exams]);

  const toggleMonth = (name) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  useEffect(() => {
    const fetchExamsAndSettings = async () => {
      if (!db) { setLoading(false); return; }
      try {
        // Fetch exams
        const snap = await getDocs(collection(db, 'exams'));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        
        // Sort by examDate ascending (fallback to deadline)
        data.sort((a, b) => {
          const dateA = a.examDate ? new Date(a.examDate).getTime() : (a.deadline ? new Date(a.deadline).getTime() : 0);
          const dateB = b.examDate ? new Date(b.examDate).getTime() : (b.deadline ? new Date(b.deadline).getTime() : 0);
          return dateA - dateB;
        });
        
        setExams(data);

        // Fetch settings
        const settingsSnap = await getDoc(doc(db, 'settings', 'exams'));
        if (settingsSnap.exists()) {
          setCalendarImg(settingsSnap.data().calendarImageURL || null);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally { setLoading(false); }
    };
    fetchExamsAndSettings();
  }, []);

  return (
    <div className="min-h-screen bg-surface">
      <Navbar />

      {/* ── Hero ── */}
      <div className="relative pt-16 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1600&auto=format&fit=crop&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-24">
          <div className="inline-flex items-center gap-2 bg-primary-container/90 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full mb-4 sm:mb-6">
            <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white fill-white" />
            <span className="text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider">Examinations</span>
          </div>
          <h1 className="font-display font-extrabold text-white mb-3 sm:mb-4" style={{ fontSize: 'clamp(1.8rem, 5vw, 3.5rem)', letterSpacing: '-0.02em' }}>
            DELE Exam <span className="text-primary-container">Calendar</span>
          </h1>
          <p className="text-white/80 text-sm sm:text-lg max-w-2xl leading-relaxed">
            Official DELE examination sessions administered by Instituto Cervantes on behalf of the Spanish Ministry of Education.
          </p>
        </div>
      </div>

      {/* ── Info Strip ── */}
      <div className="bg-white border-b border-surface-high shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-wrap gap-3 sm:gap-8">
            {[
              { Icon: Award, text: 'Globally recognized by 100+ countries' },
              { Icon: CheckCircle, text: 'Authorized Instituto Cervantes exam center' },
              { Icon: Calendar, text: 'All official examination sessions every year' },
              { Icon: Globe, text: "Jaipur's only authorized DELE exam venue" },
            ].map(({ Icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-primary-container flex-shrink-0" />
                <span className="text-xs sm:text-sm text-onSurface">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Exam Cards & Calendar Image ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        
        {/* Admin uploaded global calendar image */}
        {calendarImg && (
          <div className="mb-12 rounded-3xl overflow-hidden shadow-float border border-surface-high bg-white">
            {/* Card header */}
            <div className="hero-gradient px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-display font-bold text-sm sm:text-base leading-tight">DELE Exam Calendar</p>
                  <p className="text-white/70 text-[10px] sm:text-xs">Official session schedule — Instituto Cervantes</p>
                </div>
              </div>
              <span className="hidden sm:flex items-center gap-1.5 bg-white/15 border border-white/25 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                <CheckCircle className="w-3 h-3" /> Published
              </span>
            </div>

            {/* Image */}
            <div className="relative bg-surface-low">
              <img
                src={calendarImg}
                alt="DELE Exam Calendar"
                className="w-full h-auto object-contain max-h-[680px]"
              />
              {/* Subtle bottom fade */}
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/40 to-transparent pointer-events-none" />
            </div>

            {/* Footer note: Important Exam Policy */}
            <div className="p-6 sm:p-8 bg-surface-low border-t border-surface-high">
              <div className="flex items-start gap-3 mb-4">
                <Award className="w-5 h-5 text-primary-container flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-display font-extrabold text-sm sm:text-base text-onSurface">Important Exam Policy:</h4>
                  <p className="text-xs text-onSurfaceVariant mt-0.5">Please review the official regulations regarding written and oral exams below.</p>
                </div>
              </div>
              <div className="grid gap-4 text-xs sm:text-sm text-onSurfaceVariant ml-0 sm:ml-8">
                <div className="flex gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-container flex-shrink-0 mt-2" />
                  <p className="leading-relaxed">
                    <strong className="text-onSurface font-bold">Written Exams:</strong> Written exam dates are set globally by the Instituto Cervantes and cannot be changed under any circumstances.
                    <span className="block mt-1">
                      Written exams begin at 9:00 a.m., but the presence of the candidate is required at the exam center at 08:30 h. to verify your documentation and to receive precise instructions.
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-container flex-shrink-0 mt-2" />
                  <p className="leading-relaxed">
                    <strong className="text-onSurface font-bold">Oral Exams:</strong> The oral exams vary according to the number of candidates and test may be scheduled up to 2 days before or 2 days after the official written exam date.
                  </p>
                </div>
                <div className="flex gap-2.5 p-4 bg-primary-light/20 border-l-4 border-primary-container rounded-r-xl mt-2">
                  <p className="leading-relaxed text-onSurface">
                    <strong className="text-primary-dark font-extrabold">Mandatory Action:</strong> Your exact oral schedule will be sent via email. If you have not received your timetable details 15 days prior to the exam, please contact our center administration immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-4 mb-8 sm:mb-10">
          <div className="accent-line" />
          <h2 className="font-display font-bold text-xl sm:text-2xl">Upcoming Sessions</h2>
        </div>

        {loading ? (
          <div className="space-y-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-surface-high p-6 space-y-4 shadow-sm">
                <div className="h-8 w-1/3 bg-surface-high rounded animate-pulse" />
                <div className="grid md:grid-cols-3 gap-6">
                  {[...Array(3)].map((_, j) => <div key={j} className="h-64 bg-surface-low rounded-xl animate-pulse" />)}
                </div>
              </div>
            ))}
          </div>
        ) : exams.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl shadow-card">
            <Calendar className="w-16 h-16 text-surface-high mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl mb-2">No upcoming exams</h3>
            <p className="text-onSurfaceVariant text-sm">Exam sessions will appear here once published.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupedExams.map((group) => {
              const isExpanded = !!expandedMonths[group.name];
              const openCount = group.exams.filter((exam) => new Date() < new Date(exam.deadline)).length;

              return (
                <div key={group.name} className="bg-white rounded-2xl border border-surface-high shadow-card overflow-hidden transition-all duration-300">
                  {/* Accordion Header */}
                  <button
                    onClick={() => toggleMonth(group.name)}
                    className="w-full px-6 py-5 flex items-center justify-between hover:bg-surface-low/30 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary-light/20 flex items-center justify-center text-primary-container">
                        <Calendar className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-lg sm:text-xl text-onSurface">{group.name}</h3>
                        <p className="text-xs text-onSurfaceVariant">
                          {group.exams.length} {group.exams.length === 1 ? 'session' : 'sessions'}
                          {openCount > 0 && ` • ${openCount} open for registration`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {openCount > 0 && (
                        <span className="hidden sm:inline-block px-2.5 py-1 bg-green-50 text-green-700 text-xs font-bold rounded-full border border-green-200">
                          Active
                        </span>
                      )}
                      <div className="w-8 h-8 rounded-lg bg-surface-low flex items-center justify-center text-onSurfaceVariant hover:bg-surface-high transition-colors">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                  </button>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <div className="p-6 border-t border-surface-high bg-surface-low/10">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {group.exams.map((exam) => (
                          <ExamCard key={exam.id} exam={exam} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* About + Fees */}
        <div className="mt-10 sm:mt-16 grid sm:grid-cols-2 gap-5 sm:gap-8">
          {/* About */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-primary-container" />
                <h3 className="font-display font-bold text-lg sm:text-xl">About DELE Examinations</h3>
              </div>
              <p className="text-onSurfaceVariant text-sm leading-relaxed mb-4">
                DELE (Diplomas de Español como Lengua Extranjera) are official certificates of Spanish language proficiency, awarded by Instituto Cervantes on behalf of the Spanish Ministry of Education. They are internationally recognized and valid for life.
              </p>
              <p className="text-onSurfaceVariant text-sm leading-relaxed">
                DELE Jaipur is Rajasthan’s only official DELE exam center—take the world‑recognized Spanish exam right here in Jaipur, no need to travel out of state!
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-6 text-primary-container font-bold text-sm hover:gap-3 transition-all duration-200">
              Speak with an Advisor <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Fees */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-card flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <Award className="w-6 h-6 text-primary-container" />
                <h3 className="font-display font-bold text-xl">Eligibility & Fees</h3>
              </div>

              {/* Category selector tabs */}
              <div className="flex p-1 bg-surface-low rounded-xl mb-6 border border-surface-high">
                <button
                  onClick={() => setFeeCategory('generales')}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
                    feeCategory === 'generales'
                      ? 'bg-primary-container text-white shadow-soft'
                      : 'text-secondary hover:text-onSurface hover:bg-surface-container/50'
                  }`}
                >
                  Generales (Adults)
                </button>
                <button
                  onClick={() => setFeeCategory('escolares')}
                  className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all duration-300 ${
                    feeCategory === 'escolares'
                      ? 'bg-primary-container text-white shadow-soft'
                      : 'text-secondary hover:text-onSurface hover:bg-surface-container/50'
                  }`}
                >
                  Escolares (Minors)
                </button>
              </div>

              {/* Fees list */}
              <div className="space-y-3">
                {DELE_FEES[feeCategory].map(({ level, base, centre, total }) => (
                  <div key={level} className="flex items-center justify-between p-3 bg-surface-low rounded-xl border border-surface-container/60 hover:border-primary-light/30 transition-all duration-200">
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 bg-primary-light/40 text-primary-dark text-[10px] font-extrabold rounded">
                          {level}
                        </span>
                      </div>
                      <span className="text-[10px] text-onSurfaceVariant leading-none">
                        Exam Fees: {base} + Center Charges: {centre}
                      </span>
                    </div>
                    <span className="font-display font-bold text-sm sm:text-base text-onSurface">
                      {total}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Red Band ── */}
      <section className="hero-gradient py-10 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-white text-2xl sm:text-3xl mb-3 sm:mb-4">
            Ready to Take the DELE Exam?
          </h2>
          <p className="text-white/80 text-sm sm:text-base mb-6 sm:mb-8">
            Speak with our academic advisors to plan your DELE journey and secure your spot in the next examination session.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-primary-container font-bold rounded-xl hover:bg-primary-light transition-all duration-200 text-sm sm:text-base">
            Register Your Interest <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
